<template>
  <div v-if="isInviteExpired">
    <p class="text-center text-error">
      Invitation has expired
    </p>
  </div>

  <form v-else class="space-y-6" @submit="onSubmit">
    <div class="flex justify-center p-4">
      <IconBeepaidCustomers />
    </div>

    <FormInputText name="firstName" label="First Name" />

    <FormInputText name="lastName" label="Last Name" />

    <FormInputText name="email" label="Email" disabled />

    <FormInputText name="phone" label="Phone Number" />

    <FormInputPassword name="password" label="Password" />
    <FormInputPassword
      name="passwordConfirm"
      label="Confirm Password"
    />

    <FormInputCheckbox name="terms">
      <template #label>
        By checking this you confirm that you have read and
        agree to the
        <RouterLink
          :to="{ name: 'termsAndConditions' }"
          class="text-primary"
        >
          Terms and Conditions
        </RouterLink>
      </template>
    </FormInputCheckbox>

    <FormSubmitButton
      class="w-full"
      :is-loading="isSubmitting"
    >
      Create Account
    </FormSubmitButton>
  </form>
</template>

<script setup lang="ts">
import * as z from "zod"
import { useForm } from "vee-validate"
import { toTypedSchema } from "@vee-validate/zod"
import router from "@/router"

const props = defineProps<{
  inviteToken: string
}>()

const createEditUserFormSchema = z
  .object({
    firstName: z
      .string()
      .min(1, "First name must not be empty"),
    lastName: z
      .string()
      .min(1, "Last name must not be empty"),
    email: z
      .string()
      .email("Please enter a valid email address"),
    phone: z.string().nullable(),
    password: z
      .string()
      .min(1, "Password must not be empty"),
    passwordConfirm: z.string(),
    terms: z.boolean(),
  })
  .superRefine((params, ctx) => {
    if (params.password !== params.passwordConfirm) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Passwords must match",
        path: ["passwordConfirm"],
      })
    }

    if (params.terms !== true) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please accept the terms and conditions",
        path: ["terms"],
      })
    }
  })

const { handleSubmit, isSubmitting, resetForm, setErrors } =
  useForm({
    validationSchema: toTypedSchema(
      createEditUserFormSchema
    ),
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      passwordConfirm: "",
      terms: false,
    },
  })

const onSubmit = handleSubmit(async (params) => {
  try {
    await acceptInvite(props.inviteToken, params)
    toast.success("User Created")
    router.push({
      name: "home",
    })
  } catch (error) {
    if (error instanceof ApiError) {
      // error.code isn't set since it's not a ninja error
      if (error.detail === "Token expired") {
        isInviteExpired.value = true
      } else {
        setErrors(error.requestErrors?.body ?? {})
      }
    }
    toast.error("Failed to create account")
  }
})

const isInviteExpired = ref(false)
const isRetrievingUser = ref(false)
onMounted(async () => {
  try {
    isRetrievingUser.value = true

    resetForm({
      values: await retrieveInvite(props.inviteToken),
    })
  } catch (error) {
    if (
      error instanceof ApiError &&
      error.detail === "Token expired"
    ) {
      isInviteExpired.value = true
      return
    }
    toast.error("Invite not found")
    router.push({
      name: "logIn",
    })
  } finally {
    isRetrievingUser.value = false
  }
})
</script>
